import { format } from "date-fns";
import { useEffect, useState } from "react";

import { CalendarDateRange, Column, Label, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useBcbaVideosReviewedRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

type videosReviewedRawDataType = {
  clientId: number;
  clientName: string;
  totalVideos?: number;
  videosReviewed: number;
  percentReviewed?: number;
  sessionViewedBy: string;
};

interface AvgVideosReviewedRawDataProps {
  avgVideosReviewed: boolean;
}

export const AvgVideosReviewedRawData: React.FC<AvgVideosReviewedRawDataProps> = ({ avgVideosReviewed }) => {
  const { isUserBCBA } = useAdminData();
  const { selectedDates, allBCBAList, selectedBCBA: selectedBCBAList } = useVideoAnalytics();
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedDates);
  const [selectedBCBA, setSelectedBCBA] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedBCBA, selectedClient]);

  const { data, isFetching } = useBcbaVideosReviewedRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      bcbaProviderIds: selectedBCBA.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
      avgVideosReviewed,
    },
    {
      queryKey: ["videosReviewedRawData", dates, selectedBCBA, pageNo, pageSize, selectedClient],
    },
  );

  const columns: Column<videosReviewedRawDataType>[] = [
    {
      title: "Client ID",
      key: "clientId",
      dataIndex: "clientId",
    },
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Total Videos",
      key: "totalVideos",
      dataIndex: "totalVideos",
    },
    {
      title: "Videos Reviewed",
      key: "videosReviewed",
      dataIndex: "videosReviewed",
    },
    {
      title: "% Reviewed",
      key: "percentageReviewed",
      dataIndex: "percentReviewed",
    },
    {
      title: "BCBA",
      key: "bcba",
      dataIndex: "sessionViewedBy",
      render: (value) => (value ? value : "-"),
    },
  ];

  const filteredColumn = avgVideosReviewed
    ? columns.filter((item) => item.dataIndex !== "totalVideos" && item.dataIndex !== "percentReviewed")
    : columns;
  return (
    <div className="grid space-y-2">
      <div className="flex flex-row justify-between items-center">
        <DialogFilterBar
          dates={dates}
          setDates={setDates}
          selectedBCBA={selectedBCBA}
          setSelectedBCBA={setSelectedBCBA}
          selectedClients={selectedClient}
          setSelectedClients={setSelectedClient}
          showRBTFilter={false}
        />
        {avgVideosReviewed && !isUserBCBA ? (
          <div className="flex items-center justify-between gap-1 pr-1">
            <Label size="small">Total BCBA's:</Label>{" "}
            <Label size="small" colorType="primary">
              {allBCBAList.length}
            </Label>
          </div>
        ) : null}
      </div>
      <RawDataTableWrapper
        data={
          data && data.videosReviewedRawData?.rawData
            ? (data.videosReviewedRawData?.rawData as videosReviewedRawDataType[])
            : []
        }
        columns={filteredColumn}
        loading={isFetching}
        totalItemCount={data?.videosReviewedRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
